// ---------------------------------------------------------------------------
// Sprint 11 A2: Morning Briefing Page — Newspaper-Style Layout
// ---------------------------------------------------------------------------

import { useApi } from "../../hooks/use-api";
import { BriefingHeadlineWidget } from "../../components/briefing/headline";
import { BriefingPriorities } from "../../components/briefing/priorities";
import { BriefingMetricsPanel } from "../../components/briefing/metrics-panel";
import { BriefingIndustryIntel } from "../../components/briefing/industry-intel";
import { BriefingAiInsights } from "../../components/briefing/ai-insights";
import { BriefingOvernightEvents } from "../../components/briefing/overnight-events";

interface BriefingData {
  headline: {
    pipelineValueCents: number;
    closingThisWeek: number;
    closingValueCents: number;
    atRiskCount: number;
    newLeadsOvernight: number;
    summary: string;
  };
  priorities: Array<{
    rank: number;
    type: string;
    entityId: string;
    title: string;
    description: string;
    revenueImpactCents: number;
    suggestedAction: string;
    actionUrl: string;
  }>;
  overnightEvents: Array<{
    type: string;
    title: string;
    description: string;
    timestamp: string;
    revenueImpactCents: number;
  }>;
  metrics: {
    leadsToday: number;
    leadsYesterday: number;
    leadsTrend: "up" | "down" | "flat";
    revenueThisWeekCents: number;
    revenueLastWeekCents: number;
    revenueTrend: "up" | "down" | "flat";
    openRate: number;
    openRateYesterday: number;
    conversionRate: number;
    conversionRateYesterday: number;
    avgResponseTimeHours: number;
  } | null;
  industryComparisons: Array<{
    metric: string;
    yourValue: number;
    industryAvg: number | null;
    top10Percent: number | null;
    unit: string;
  }>;
  aiInsights: string[];
}

const EMPTY_HEADLINE = {
  pipelineValueCents: 0,
  closingThisWeek: 0,
  closingValueCents: 0,
  atRiskCount: 0,
  newLeadsOvernight: 0,
  summary: "",
};

export function BriefingPage() {
  const { data: briefing, isLoading } = useApi<BriefingData>("/v1/intelligence/briefing/today");

  const headline = briefing?.headline ?? EMPTY_HEADLINE;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Newspaper header */}
      <div className="text-center border-b-4 border-double border-[var(--color-foreground)] pb-4 mb-6">
        <div className="text-xs text-[var(--color-muted-foreground)] uppercase tracking-widest">
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </div>
        <h1 className="text-3xl font-bold text-[var(--color-foreground)] mt-1">
          Your Morning Briefing
        </h1>
        <div className="text-xs text-[var(--color-muted-foreground)] mt-1">AI-generated intelligence report</div>
      </div>

      {isLoading ? (
        <div className="text-center text-[var(--color-muted-foreground)] py-12">Loading briefing...</div>
      ) : (
        <>
          {/* Headline — pipeline summary */}
          <BriefingHeadlineWidget
            pipelineValueCents={headline.pipelineValueCents}
            closingThisWeek={headline.closingThisWeek}
            closingValueCents={headline.closingValueCents}
            atRiskCount={headline.atRiskCount}
            newLeadsOvernight={headline.newLeadsOvernight}
            summary={headline.summary}
          />

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Main column — priorities + events */}
            <div className="lg:col-span-2 space-y-6">
              <BriefingPriorities priorities={briefing?.priorities ?? []} />
              <BriefingOvernightEvents events={briefing?.overnightEvents ?? []} />
            </div>

            {/* Sidebar — metrics + intel + AI */}
            <div className="space-y-6">
              <BriefingMetricsPanel metrics={briefing?.metrics ?? null} />
              <BriefingIndustryIntel comparisons={briefing?.industryComparisons ?? []} />
              <BriefingAiInsights insights={briefing?.aiInsights ?? []} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
